"use client";

import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Check, Copy, Play } from "lucide-react";
import { SectionHeading, metalText } from "./chrome";

export const MCP_URL = "https://quantwheel.com/api/mcp";

/** Hero video panel - YouTube embed, self-hosted mp4, or placeholder. */
export function McpVideoPlaceholder({
  videoUrl,
  posterUrl,
  youtubeEmbedUrl,
  label,
}: {
  videoUrl?: string | null;
  posterUrl?: string | null;
  youtubeEmbedUrl?: string | null;
  label: string;
}) {
  return (
    <div className="relative mx-auto mt-12 w-full max-w-5xl md:mt-16">
      <div
        className="pointer-events-none absolute -inset-12 rounded-full opacity-20"
        style={{
          background: "radial-gradient(ellipse, rgba(16,185,129,0.3) 0%, rgba(154,160,158,0.08) 45%, transparent 70%)",
        }}
        aria-hidden="true"
      />
      <div className="relative overflow-hidden rounded-xl border border-white/10 bg-[#101111] shadow-2xl shadow-black/60">
        {youtubeEmbedUrl ? (
          <iframe
            src={youtubeEmbedUrl}
            title={label}
            className="aspect-video w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : videoUrl ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            poster={posterUrl ?? undefined}
            className="w-full"
            aria-label={label}
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        ) : (
          <div className="flex aspect-video w-full flex-col items-center justify-center gap-4">
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#3a3c3b] bg-gradient-to-b from-[#1c1e1d] to-[#131414] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
              <Play className="ml-0.5 h-6 w-6 text-[#e8eae9]" />
            </span>
            <p className="text-sm text-white/50">Demo video coming soon</p>
            <p className="max-w-md text-center text-xs text-white/40">{label}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/** The MCP endpoint URL with a copy-to-clipboard button. */
export function McpUrlChip() {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(MCP_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (e.g. non-secure context); the URL is still selectable.
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="group inline-flex max-w-full items-center gap-2.5 rounded-lg border border-[#3a3c3b] bg-[#0d0e0e] px-4 py-2.5 font-mono text-sm text-[#e8eae9] transition-colors hover:border-[#4a4c4b]"
      aria-label="Copy MCP server URL"
    >
      <span className="truncate">{MCP_URL}</span>
      {copied ? (
        <Check className="h-4 w-4 shrink-0 text-emerald-400" />
      ) : (
        <Copy className="h-4 w-4 shrink-0 text-[#8d918f] group-hover:text-[#d4d5d5]" />
      )}
    </button>
  );
}

/**
 * Step-by-step instructions for adding the QuantWheel MCP server to Claude
 * as a custom connector. Shared by all MCP landing pages.
 */
export function ClaudeConnectSteps({ toolsHint }: { toolsHint: string }) {
  const steps: { title: string; description: React.ReactNode }[] = [
    {
      title: "Open Claude connector settings",
      description: (
        <>
          In Claude (web or desktop), go to <span className="text-[#d4d5d5]">Settings &rarr; Connectors</span> and
          click <span className="text-[#d4d5d5]">Add custom connector</span>.
        </>
      ),
    },
    {
      title: "Paste the QuantWheel MCP URL",
      description: (
        <>
          Name it <span className="text-[#d4d5d5]">QuantWheel</span> and paste the server URL below, then click{" "}
          <span className="text-[#d4d5d5]">Add</span>.
        </>
      ),
    },
    {
      title: "Connect and authenticate",
      description: (
        <>
          Click <span className="text-[#d4d5d5]">Connect</span> and sign in with your QuantWheel account. OAuth
          links your subscription - no keys to paste.
        </>
      ),
    },
    {
      title: "Ask away",
      description: <>Claude can now call {toolsHint} - live, on real market data, in plain English.</>,
    },
  ];

  return (
    <div id="connect" className="mt-20 scroll-mt-24 sm:mt-24">
      <SectionHeading
        title={
          <>
            Add it to Claude <span style={metalText}>in under a minute</span>
          </>
        }
        sub="QuantWheel ships as a remote MCP server. Add it as a custom connector once and every conversation can pull real-time market data."
      />
      <Card className="mx-auto mt-10 max-w-3xl border-[#1f2020] bg-[#101111] p-6 sm:p-8">
        <ol className="flex flex-col gap-6">
          {steps.map((step, i) => (
            <li key={step.title} className="flex gap-4">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#3a3c3b] bg-gradient-to-b from-[#1c1e1d] to-[#131414] text-sm text-[#d4d5d5] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                {i + 1}
              </span>
              <div className="min-w-0">
                <h3 className="text-white">{step.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-white/60">{step.description}</p>
                {i === 1 ? (
                  <div className="mt-3">
                    <McpUrlChip />
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
        <p className="mt-6 border-t border-[#1c1d1d] pt-5 text-xs leading-relaxed text-white/40">
          Not on Claude? The same URL works in ChatGPT, Cursor, or any MCP client that speaks Streamable HTTP.
          Custom agents can skip OAuth and send an{" "}
          <span className="font-mono text-white/60">Authorization: Bearer qw_live_...</span> API key instead.
        </p>
      </Card>
    </div>
  );
}

/** Row of client names the MCP server works with. */
export function WorksWithRow() {
  const clients = ["Claude", "ChatGPT", "Cursor", "Claude Code", "Custom agents"];
  return (
    <div className="mt-10 flex flex-wrap items-center justify-center gap-2.5">
      {clients.map((client) => (
        <span
          key={client}
          className="rounded-full border border-[#2b2d2c] px-4 py-1.5 text-sm text-[#8d918f]"
        >
          {client}
        </span>
      ))}
    </div>
  );
}
